import { api, ApiError } from "../api.js";
import { isAuthenticated } from "../auth.js";
import { navigate } from "../app.js";
import { rememberReturn } from "../returnTo.js";
import { esc, displayName } from "../utils.js";

/**
 * Where an emailed cohort invitation's link lands (#/cohort-invite/:token).
 *
 * Open without a session, because the link is the credential: it shows the cohort's
 * name and description and who invited, then asks the reader to log in or sign up
 * (coming back here afterwards) and accept. Accepting needs a confirmed email, as
 * joining any cohort does; until then this page says so and waits to be reopened.
 */
export async function render(container, params) {
  const token = params.token;
  const here = `/cohort-invite/${token}`;
  container.innerHTML = `<div class="text-center py-5"><div class="spinner-border text-primary"></div></div>`;

  let invite;
  try {
    invite = await api.get(`/cohort-invitations/link/${encodeURIComponent(token)}`);
  } catch (err) {
    const expired = err instanceof ApiError && err.code === "invitation_expired";
    container.innerHTML = `
      <div class="dc-empty" id="invite-unavailable">
        <i class="bi ${expired ? "bi-hourglass-bottom" : "bi-envelope-x"}"></i>
        <div class="fw-semibold">${expired ? "This invitation has expired" : "This invitation link isn't valid"}</div>
        <div>${expired
          ? "Ask the cohort's admins to invite you again."
          : "It may have been used already, or replaced by a newer invitation. Ask the cohort's admins for a new one."}</div>
      </div>`;
    return;
  }

  const cohort = invite.community;
  // The address the invitation went to: filled in on the sign-up and log-in forms,
  // so the account matches the invitation unless the reader changes it.
  const withEmail = (path) => (invite.email ? `${path}?email=${encodeURIComponent(invite.email)}` : path);
  const who = invite.invited_by ? esc(displayName(invite.invited_by)) : "A cohort admin";

  function paint(message = "") {
    const signedIn = isAuthenticated();
    container.innerHTML = `
      <div class="auth-shell">
        <div class="card auth-card p-4">
          <div class="card-body">
            <div class="text-center mb-3">
              <div class="dc-avatar dc-avatar-lg mx-auto mb-2" style="background-color:var(--dc-teal-100);">
                <i class="bi ${cohort.is_private ? "bi-lock" : "bi-people"}" style="color:var(--dc-teal-700);font-size:2rem;"></i>
              </div>
              <div class="text-slate small">${who} invited you to join</div>
              <h1 class="display-heading h4 mb-1" id="invite-cohort-name">${esc(cohort.name)}</h1>
              <div class="text-slate small">
                ${cohort.is_private ? '<span class="badge text-bg-light border me-1"><i class="bi bi-lock me-1"></i>Private</span>' : ""}
                ${cohort.member_count ?? 0} members
              </div>
            </div>
            ${cohort.description ? `<p class="dc-user-text">${esc(cohort.description)}</p>` : ""}
            ${cohort.is_private ? `<p class="text-slate small">Its posts, dive events and photos are visible only to its members.</p>` : ""}
            <div id="invite-message" class="${message ? "" : "d-none"} alert alert-warning small">${message}</div>
            ${signedIn
              ? `<button class="btn btn-primary w-100" id="invite-accept">Accept invitation</button>`
              : `<p class="small mb-2">Log in, or create a DiveCohorts account, to accept.</p>
                 <div class="d-flex gap-2">
                   <button class="btn btn-primary flex-fill" id="invite-login">Log in</button>
                   <button class="btn btn-outline-primary flex-fill" id="invite-register">Sign up</button>
                 </div>`}
          </div>
        </div>
      </div>`;

    // Back here after logging in or signing up (returnTo.js).
    container.querySelector("#invite-login")?.addEventListener("click", () => {
      rememberReturn(here);
      navigate(withEmail("/login"));
    });
    container.querySelector("#invite-register")?.addEventListener("click", () => {
      rememberReturn(here);
      navigate(withEmail("/register"));
    });
    container.querySelector("#invite-accept")?.addEventListener("click", accept);
  }

  async function accept(e) {
    const btn = e.currentTarget;
    btn.disabled = true;
    try {
      const joined = await api.post(`/cohort-invitations/link/${encodeURIComponent(token)}/accept`);
      navigate(`/cohort/${joined.id}`);
    } catch (err) {
      if (err instanceof ApiError && err.code === "email_not_verified") {
        // The confirmation link brings them back here (verifyEmail.js).
        rememberReturn(here);
        paint("Confirm your email address first: open the link we emailed you, then accept here.");
      } else {
        paint(esc(err instanceof ApiError ? err.message : "Something went wrong."));
      }
    }
  }

  paint();
}
